import { FileType } from '@/constants/FileTypes'
import { basePath } from '@/next.config'
import contentfulClient from '@/utils/contentful/client'
import filterTracksWithSameMix from '@/utils/filterTracksWithSameMix'
import getDownloadFiles from './utils/getDownloadFiles'
import { getNewRelicTraceHeaders } from '@/components/app/NewRelic/NewRelicAgent'
import getRequest from './utils/getRequest'
import logger from './utils/logger'

export const clientGetTracks = async (trackIds: number[]): Promise<Track[]> => {
	const tracks = getRequest<Track[]>(
		`${basePath}/api/musicapp/music/tracks?trackIds=${trackIds}`,
		'Tracks'
	)

	return tracks
}

export const clientGetAlbumsWithCount = async (
	page: number,
	locale: string,
	genreId?: number
): Promise<AlbumResponse> => {
	const albums = getRequest<AlbumResponse>(
		`${basePath}/api/musicapp/music/albums?page=${page}&locale=${locale}${
			genreId ? `&genre=${genreId}` : ''
		}`,
		'Albums'
	)

	return albums
}

export const clientGetSimilarTracks = async (
	trackIds: number[]
): Promise<Track[] | []> => {
	const tracks = await getRequest<number[]>(
		`${basePath}/api/musicapp/aims/similar?trackIds=${trackIds}`,
		'aims similar tracks'
	)

	const results = await clientGetTracks(tracks)

	return filterTracksWithSameMix(results)
}

export const clientGetSearchSuggestions = async (
	query: string
): Promise<{ label: string | JSX.Element; value: string }[]> => {
	const results = await getRequest<{ label: string; value: string }[]>(
		`${basePath}/api/musicapp/search/autocomplete?term=${query}`,
		'search suggestions'
	)

	if (!results || results.length == 0) {
		return []
	}

	const uniqueResults = results.filter(
		(result, index, self) =>
			index === self.findIndex((r) => r.value === result.value)
	)
	return uniqueResults
}

export const clientGetStreamingServiceTrackInformation = async (
	url: string
): Promise<StreamingServiceTrackInformation> => {
	const information = getRequest<StreamingServiceTrackInformation>(
		`${basePath}/api/musicapp/streaming-services?url=${encodeURIComponent(
			url
		)}`,
		'Streaming Service Track Information'
	)

	return information
}

export const clientSearchByAudio = async (url: string): Promise<string[]> => {
	const trackIds = getRequest<string[]>(
		`${basePath}/api/musicapp/aims/url?link=${encodeURIComponent(url)}`,
		'Search By Audio'
	)

	return trackIds
}

export const clientPromptSearch = async (text: string): Promise<string[]> => {
	const trackIds = await getRequest<string[]>(
		`${basePath}/api/musicapp/aims/prompt?text=${encodeURIComponent(text)}`,
		'Prompt Search'
	)

	return trackIds || []
}

export const clientDownloadStems = async (track: Track): Promise<Response> => {
	const { id, title, album, albumTrackNumber } = track
	const response = getRequest<string>(
		`${basePath}/api/musicapp/downloads/stems?trackId=${id}&trackTitle=${encodeURIComponent(
			title
		)}&albumNumber=${album.number}&albumTrackNumber=${albumTrackNumber}`,
		'Stems Download',
		{
			headers: getNewRelicTraceHeaders(),
		}
	)

	const url: string = await response

	const files = await getDownloadFiles(url)

	return files
}

export const clientUpdateDownloadCount = async (
	tracks: Track | Track[],
	fileType: string,
	userId: number,
	organisationId: number,
	stemsTrackCount?: number
): Promise<void> => {
	const isBulkDownload = Array.isArray(tracks)
	isBulkDownload ? tracks : [tracks]

	const body = {
		TrackIds: isBulkDownload ? tracks.map((track) => track.id) : [tracks.id],
		FileType: FileType[fileType.toUpperCase()],
		OrganisationId: organisationId,
		UserId: userId,
		stemsTrackCount,
	}

	getRequest(
		`${basePath}/api/musicapp/downloads/${organisationId}`,
		'Download',
		{
			body: JSON.stringify(body),
			method: 'PATCH',
		}
	)
}

export const clientGetContentfulAlbums = async (
	albumNumbers: number[]
): Promise<ContentfulAlbum[]> => {
	let response: Response
	try {
		response = await contentfulClient(`
			albumCollection(where: { OR: [${albumNumbers.map((albumNumber) => {
				return `{ id: ${albumNumber} }`
			})}]}) {
				items {
					releaseDate,
					trackId,
					id,
					title,
					shortDescription,
					longDescription,
					artwork {
						url
					}
				}
			}
		`)

		if (!response.ok) {
			throw new Error(response.status.toString())
		}

		const result = await response.json()
		return result.data.albumCollection.items.map((item) => {
			return { ...item, albumNumber: item.id, url: item.artwork?.url }
		})
	} catch (error) {
		logger('Error retrieving album artwork', error, {
			errorType: 'Contentful',
			contentType: 'album artwork',
			contentId: albumNumbers.toString(),
			fetchUrl: response?.url,
			fetchStatus: response?.status,
		})
		return null
	}
}
